package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.css.VegasfulStyles
import com.steamstreet.vegasful.css.css
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.css.*
import kotlinx.html.dom.append
import kotlinx.html.js.div
import kotlinx.html.js.span
import org.w3c.dom.HTMLElement

/**
 * How long a notification stays fully visible before it begins to fade.
 */
private const val VISIBLE_MS = 4000

/**
 * The length of the fade. This must match the CSS transition duration below,
 * because the element is removed from the document once the fade has run.
 */
private const val FADE_MS = 300

private var current: HTMLElement? = null
private var fadeTimer: Int? = null
private var removeTimer: Int? = null

/**
 * Show a transient message near the bottom of the window. The notification
 * fades out and removes itself, so callers do not have to manage its lifetime.
 * A new notification replaces one that is still on screen, so repeated actions
 * never stack up.
 *
 * This mirrors the confirmation the mobile app shows for the same actions.
 */
fun notify(message: String) {
    dismissCurrent()

    val element = document.body?.append {
        div {
            css {
                position = Position.fixed
                left = 0.px
                right = 0.px
                bottom = 24.px
                zIndex = VegasfulStyles.zIndexMenu + 1
                textAlign = TextAlign.center
                padding = Padding(0.px, 16.px)

                // The notification is purely informational, so it must never
                // intercept clicks meant for the page underneath it.
                declarations["pointer-events"] = "none"
                declarations["transition"] = "opacity ${FADE_MS}ms ease-in-out"
            }

            span {
                css {
                    +VegasfulStyles.popupWindow
                    display = Display.inlineBlock
                    color = VegasfulStyles.white
                    fontSize = 0.9.em
                    maxWidth = 90.pct
                    padding = Padding(10.px, 16.px)
                }
                +message
            }
        }
    }?.firstOrNull() ?: return

    // Start transparent before the browser paints, so the element fades in
    // rather than appearing at full opacity and then transitioning.
    element.style.opacity = "0"
    current = element

    window.requestAnimationFrame {
        if (current === element) {
            element.style.opacity = "1"
        }
    }

    fadeTimer = window.setTimeout({
        element.style.opacity = "0"
        removeTimer = window.setTimeout({
            element.remove()
            if (current === element) {
                current = null
            }
        }, FADE_MS)
    }, VISIBLE_MS)
}

/**
 * Remove any notification that is still on screen, along with its pending
 * timers, so a replacement starts from a clean state.
 */
private fun dismissCurrent() {
    fadeTimer?.let { window.clearTimeout(it) }
    removeTimer?.let { window.clearTimeout(it) }
    fadeTimer = null
    removeTimer = null
    current?.remove()
    current = null
}
